<template lang="pug">
  transition(name="slide-fade-left")
    .sidecart__line-item(v-if="product", :class="itemClass", :data-variantId="item.id")
      .sidecart__line-item__image-wrapper
        img.sidecart__line-item__image(:src="thumbnail", :alt="item.product_title")
      .sidecart__line-item__content
        .sidecart__line-item__details(:data-handle="item.handle")
          p.sidecart__line-item__title {{ cleanedTitle }}
          p.sidecart__line-item__price
            span(v-if="!isDiscounted" :class="{ 'sidecart__line-item__price--discount': isDiscounted }") {{ item.line_price | money }}
            span.sidecart__line-item__price--compare-at(v-if="isDiscounted") {{ originalPrice | money }}
            span.sidecart__line-item__price--discount(v-if="isDiscounted") {{ comparePrice | money }}
            span.sidecart__line-item__price--discount-percent(v-if="isDiscounted") {{ savingsText }}
          p.sidecart__line-item__variant(v-if="hasVariantTitle") {{ cleanedVariantTitle }}
          p.sidecart__line-item__frequency(v-if="purchaseFrequency")
            icon.sidecart__line-item__frequency-icon(
              name="reoccuring"
              width="16px"
              height="16px"
            )
            | {{ purchaseFrequency }}
        .sidecart__line-item__controls
          icon-button.sidecart__line-item__remove(:label="'Remove ' + item.product_title" icon="close" width="10px" height="10px" @click="removeItem")
          sidecart-quantity-selector(
            :quantity="item.quantity"
            :disabled-increment="isGiftWithPurchase"
            @increment="increment"
            @decrement="decrement"
          )
</template>

<script>
  import { mapState } from 'vuex';
  import { debounce } from 'scripts/helpers/util.js';
  import { getDiscountPercentage, getProductCleanTitle } from 'scripts/helpers/product.js';

  import Icon from 'scripts/components/basic/Icon.vue';
  import IconButton from 'scripts/components/buttons/IconButton.vue';
  import SidecartQuantitySelector from 'scripts/components/cart/SidecartQuantitySelector.vue';

  export default {
    name: 'SidecartLineItem',
    components: {
      Icon,
      IconButton,
      SidecartQuantitySelector,
    },
    props: {
      item: Object,
      line: Number,
    },
    data () {
      return {
        updating: false,
      };
    },
    computed: {
      ...mapState({
        allProducts: state => state.products.all,
      }),
      itemClass () {
        return {
          'updating': this.updating,
        };
      },
      cleanedTitle () {
        if (!this.product) {
          return this.item.product_title || '';
        }
        return getProductCleanTitle(this.product.title);
      },
      cleanedVariantTitle () {
        if (!this.variant || !this.variant.title) {
          return '';
        }
        return this.variant.title.replace('/', '|');
      },
      savingsText() {
        // Check if item has selling plan with discount
        if (this.item && this.item.selling_plan_allocation) {
          // Try to get discount from selling plan
          const adjustments = this.item.selling_plan_allocation.price_adjustments;
          const discount = (adjustments && adjustments[0] && adjustments[0].value) || 10;
          return `${discount}% Savings`;
        }
        return '';
      },
      originalPrice() {
        // For selling plan items, calculate the original price from the variant
        if (this.item.selling_plan_allocation) {
          // Get the variant price and multiply by quantity
          if (this.product) {
            var variant = this.product.variants.find(function(v) { return v.id === this.item.variant_id; }.bind(this));
            if (variant) {
              return variant.price * this.item.quantity;
            }
          }
        }
        // Use Shopify's native original price if available
        if (this.item.original_line_price !== undefined && this.item.original_line_price !== this.item.line_price) {
          return this.item.original_line_price;
        }
        // Fallback to line price if no original price provided
        return this.item.line_price;
      },
      product () {
        var self = this;
        return this.allProducts.find(function(product) {
          return Boolean(product.variants.find(function(variant) { return self.item.id === variant.id; }));
        });
      },
      thumbnail () {
        // First try to get from product data
        if (this.product && this.product.thumbnail) {
          return this.product.thumbnail;
        }
        // Fallback to item's image property
        if (this.item && this.item.image) {
          return this.item.image;
        }
        // Fallback to featured_image if available
        if (this.item && this.item.featured_image && this.item.featured_image.url) {
          return this.item.featured_image.url;
        }
        return '';
      },
      variant () {
        return (this.product) ? this.product.variants.find((variant) => variant.id === this.item.id) : null;
      },
      comparePrice () {
        // Use final_line_price if available (selling plans)
        if (this.item.final_line_price !== undefined) {
          return this.item.final_line_price;
        }
        // Fallback to line_price
        return this.item.line_price;
      },
      isDiscounted () {
        // Check for selling plan discount
        if (this.item && this.item.selling_plan_allocation) {
          return true;
        }
        // Check if original price differs from final price
        if (this.item && this.item.original_line_price && this.item.original_line_price !== this.item.final_line_price) {
          return true;
        }
        return false;
      },
      discountPercentage () {
        return (this.isDiscounted)
          ? getDiscountPercentage(this.item.line_price, this.comparePrice)
          : 0;
      },
      isGiftWithPurchase () {
        return Boolean(this.item.properties && this.item.properties._gwp);
      },
      isSubscription () {
        // Check for selling plan (new way)
        if (this.item && this.item.selling_plan_allocation) {
          return true;
        }
        // Check for legacy subscription
        return Boolean(this.item && this.item.properties && this.item.properties.subscription_id);
      },
      hasVariantTitle () {
        return this.variant && this.variant.title !== 'Default Title';
      },
      purchaseFrequency () {
        // Get frequency from selling plan if available
        if (this.item && this.item.selling_plan_allocation && this.item.selling_plan_allocation.selling_plan) {
          return this.item.selling_plan_allocation.selling_plan.name;
        }
        // Fallback to legacy properties
        if (this.isSubscription && this.item && this.item.properties) {
          return `Every ${this.item.properties.shipping_interval_frequency} ${this.item.properties.shipping_interval_unit_type}`;
        }
        return false;
      },
    },
    created () {
      if(this.item && !this.product) {
        const handle = this.item.original_handle ? this.item.original_handle : this.item.handle;
        this.$store.dispatch('products/addProduct', handle);
      }
    },
    methods: {
      removeItem () {
        this.updating = true;
        if (this.isGiftWithPurchase) this.$emit('gwp-cancelled');

        this.$store.dispatch('cart/removeItem', this.line)
          .catch(error => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
          .then(() => this.updating = false);
      },
      increment () {
        this.updateQuantity(this.item.quantity + 1);
      },
      decrement () {
        if (this.item.quantity > 0) {
          this.updateQuantity(this.item.quantity - 1);
        }
      },
      updateQuantity: debounce(function (newQuantity) {
        this.updating = true;

        if (newQuantity < 1) {
          this.removeItem();
          return;
        }

        this.$store.dispatch('cart/updateItem', { line: this.line, quantity: newQuantity })
          .catch(error => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
          .then(() => this.updating = false);
      }, 100),
    },
  };
</script>

<style scoped lang="scss">
  .sidecart__line-item {
    display: flex;
    align-items: center;
    position: relative;
    padding: 9px 0;
    border-bottom: 1px solid $border--lightest;

    &.updating {
      opacity: 0.5;
      pointer-events: none;

      &.slide-fade-left-leave-active {
        opacity: 0;
      }
    }

    &__image-wrapper {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 80px;
      padding: 10px;
    }

    &__image {
      max-height: 100%;
      flex-shrink: 0;
    }

    &__content {
      display: flex;
      flex-grow: 1;
      padding-left: 4px;

      @include tablet-up() {
        padding-left: 24px;
      }
    }

    &__details {
      padding-right: 10px;
      flex-grow: 1;
    }

    &__controls {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      flex-shrink: 0;
    }

    &__title {
      margin: 0;
      padding-right: 8px;
      font-size: rem(13);
      line-height: 1.54;
      font-family: $font-sweet-sans;
      font-weight: 500;
      color: $color-indigo;
    }

    &__frequency,
    &__variant {
      margin: 0;
      font-size: rem(14);
      line-height: 1.28;
    }

    &__remove {
      margin-top: 5px;
    }

    .sidecart__line-item__price {
      margin: 0;
      color: $color-charcoal;

      &--free {
        color: $color--accent;
      }

      &--compare-at {
        color: $color-grey;
        text-decoration: line-through;
      }

      &--discount-percent {
        display: block;

        @include tablet-up() {
          display: inline;
        }
      }

      &--discount,
      &--compare-at {
        margin-right: 8px;
      }

      &--compare-at,
      &--discount-percent {
        font-size: rem(12);
      }
    }

    &__frequency {
      @include mobile-only {
        margin-bottom: 8px;
      }
    }
  }

  .sidecart__line-item__quantity {
    padding: 4px 6px;
    -webkit-appearance: none;
    margin-top: 14px;
  }

  .sidecart__line-item__price,
  .sidecart__line-item__variant,
  .sidecart__line-item__frequency {
    font-size: rem(13);
    line-height: 1.54;
    font-family: $font-gibson;
    color: $color-grey;
  }

  .sidecart__line-item__price--discount,
  .sidecart__line-item__price--discount-percent {
    color: $color-red;
  }

  .sidecart__line-item__frequency-icon {
    margin-right: 6px;
  }
</style>
